<script setup lang="ts">
/**
 * Appointment of DPO (B32116) — SG 일반 프리셋 Studio 패널.
 *   문서 1종(2쪽): Directors' Resolutions in Writing (Appointment of Data Protection Officer) — 이사 전원 서명([[Signers]])
 *   + Letter of Authorisation — DPO 본인 서명([[dpo_signature]]).
 *   회사 선택 → 회사명·UEN·등록사무소 주소·이사(서명자) 자동채움 → DPO(✨ 연락처: 이름·연락처·이메일) · 날짜 입력 → Create Task.
 *   [[if is_sole_director]] = 서명 이사가 1명이면 자동, [[if dpo_gender == "F"]] = 문서의 his/her 클릭(연락처 성별이 있으면 자동).
 *   Register 단계(옵션) — TASK_DATA.formData 의 contactCd(✨ DPO 연락처) · position B30001 · appointedDate(= 위임장 날짜)로
 *   Register of DPOs(/registerFromContact/dpo, A49001) + L_OFFICER(register-officer) 에 넣는다.
 */
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { sgAppliedAddressLine } from '@/features/startTask/preset_task/sg/preset/utils/sgAppliedAddressLine';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, typeOf } from './final-dividends/cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from './final-dividends/cdd/agmSignersContext';
import AppointDpoPackageDoc from './appoint-dpo/AppointDpoPackageDoc.vue';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
  selectedPerson?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  appliedAddress?: {
    key: string; line?: string; line1?: string; line2?: string; line3?: string;
    town?: string; state?: string; postcode?: string; country?: string; seq?: number;
  } | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-person-lookup', context: Record<string, any>): void;
  (e: 'request-address-input', key: string): void;
}>();

const TASK_CODE = 'B32116';
/** L_OFFICER 직책 코드 — Director / Data Protection Officer. */
const POS_DIRECTOR = 'B30027';
const POS_DPO = 'B30001';

const router = useRouter();
const fill = provideAgmFill();
const signersCtx = provideAgmSigners();
const taskConfigSteps = ref<string[]>([]);
const lastAutofillCd = ref('');
const autofilledKeys = ref<Set<string>>(new Set());

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd || '').trim(),
);
provide(AGM_ENTITY_CD, entityCd);

const effectiveTitle = computed(() => String(props.taskTitle || '').trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

const t = (v: unknown) => String(v ?? '').trim();

/** 서명 이사가 1명이면 SOLE DIRECTOR’S / the Sole Director 문구([[if is_sole_director]]). */
const soleDirector = computed(() => signersCtx.signersOf('dr_Signers').length === 1);
/** DPO 성별 — 'F' 면 her, 그 외 his([[if dpo_gender == "F"]]). */
const dpoGender = ref('M');
/** ✨ 로 고른 DPO 연락처 코드 — Register 단계(레지스터·L_OFFICER)에 필요. 이름을 직접 고치면 비운다. */
const dpoContactCd = ref('');
let dpoContactName = '';
watch(() => fill.values.dpo_name, (nm) => { if (dpoContactCd.value && t(nm) !== dpoContactName) dpoContactCd.value = ''; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  emit('progress-change', Math.min(100, Math.round((fill.filledCount.value / total) * 100)));
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

// ✨ DPO = 글로벌 연락처. 결과는 조회를 요청한 칸에 넣는다(조회 중 다른 칸 클릭에 영향받지 않게).
let lookupKey = '';
fill.setActivateHandler((key: string) => {
  if (typeOf(key) === 'entity') { emit('change-entity'); return; }
  if (key === 'dpo_name') {
    lookupKey = key;
    emit('request-person-lookup', { fieldGroup: 'dpo-appoint', scopedKey: key, defaultRole: 'CONTACT', lookupType: 'contact', subjectLabel: 'Data Protection Officer (contact)' });
  }
});
fill.setAddressHandler((key: string) => { emit('request-address-input', key); });

/** 연락처 성별 → 'F' / 'M' (없으면 '') */
function genderOf(p: Record<string, any>): string {
  const g = t(p.gender || p.GENDER || p.genderCd || p.GENDER_CD || p.sex).toUpperCase();
  if (/^F|FEMALE/.test(g)) return 'F';
  if (/^M|MALE/.test(g)) return 'M';
  return '';
}

watch(() => props.selectedPerson, (p) => {
  if (!p) return;
  const key = lookupKey || fill.activeKey.value;
  lookupKey = '';
  if (key !== 'dpo_name') return;
  const nm = t(p.name || p.contactName || p.fullName || `${p.firstName || ''} ${p.lastName || ''}`);
  if (!nm) return;
  dpoContactName = nm;
  dpoContactCd.value = t(p.contactCd || p.CONTACT_CD);
  fill.setValue('dpo_name', nm);
  // 고른 사람으로 위임장 정보도 채운다(있는 값만)
  const email = t(p.contactEmail || p.email || p.CONTACT_EMAIL);
  const mobile = t(p.mobileNo || p.MOBILE_NO || p.phone || p.contactNo);
  // 직함 — 조회 결과의 positionName 은 조회 역할 이름("Contact" 등)이라 쓰지 않는다.
  const designation = t(p.designation || p.jobTitle || p.occupation);
  if (email) fill.setValue('dpo_email', email);
  if (mobile) fill.setValue('dpo_contact_no', mobile);
  if (designation) fill.setValue('dpo_designation', designation);
  const g = genderOf(p as Record<string, any>);
  if (g) dpoGender.value = g;
  emitProgress();
});
watch(() => props.selectedAddress, (a) => {
  if (!a?.value || !fill.activeKey.value) return;
  fill.setValue(fill.activeKey.value, a.value);
  emitProgress();
});
watch(() => props.appliedAddress, (a) => {
  if (!a?.key) return;
  const line = sgAppliedAddressLine(a);
  if (line) { fill.setValue(a.key, line); emitProgress(); }
}, { deep: true });

let signerSeq = 0;
function mkSigner(name: string, title = 'Director', role = 'director'): SignerEntry {
  return { id: `id-sg-dpo-${signerSeq++}`, name: t(name), title, role, isManual: true, showTitle: !!title, _signerLayout: 'box' } as SignerEntry;
}

// ── 자동채움: 회사 선택 → 회사명·UEN·등록사무소 주소·이사(서명자) ──
async function autofillFromEntity(cdRaw: string) {
  const cd = t(cdRaw);
  if (!cd || cd === lastAutofillCd.value) return;
  const entityChanged = !!lastAutofillCd.value && lastAutofillCd.value !== cd;
  lastAutofillCd.value = cd;
  for (const k of autofilledKeys.value) fill.setValue(k, '');
  if (entityChanged) signersCtx.clearAll();
  const written = new Set<string>();
  const set = (k: string, v: unknown) => { const s = t(v); if (s) { fill.setValue(k, s); written.add(k); } };

  set('company_name', props.chatFormData?.entityName || props.entityData?.companyName);
  set('company_registration_no', props.chatFormData?.registrationNo || props.entityData?.registrationNo);
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    set('company_name', src.companyName);
    set('company_registration_no', src.registrationNo);
    set('registered_office_address', src.registeredOfficeAddress);
  } catch (e) { console.warn('[AppointDpo] agm-source failed', e); }

  seedDirectorSigners(await loadOfficers(cd));
  autofilledKeys.value = written;
  emitProgress();
}

async function loadOfficers(cd: string): Promise<any[]> {
  if (!cd) return [];
  try {
    const res: any = await docGenApi.getEntityOfficersAll(cd);
    return res?.data?.officers ?? res?.data?.data?.officers ?? [];
  } catch (e) { console.warn('[AppointDpo] officers-by-entity-all failed', e); return []; }
}

/** 이사 전원 = 결의 서명자. 이미 서명자가 있으면 건드리지 않는다. 직책명이 정확히 'Director' 인 경우만(Managing/Alternate 제외). */
function seedDirectorSigners(officers: any[]) {
  if (signersCtx.signersOf('dr_Signers').length) return;
  const names: string[] = [];
  for (const o of officers) {
    const positions: any[] = Array.isArray(o?.positions) ? o.positions : [];
    const hit = positions.some((p) => String(p?.positionCd || '') === POS_DIRECTOR || /^\s*director\s*$/i.test(String(p?.positionName || '')));
    const nm = t(o?.contactName || o?.name || o?.fullName);
    if (hit && nm && !names.includes(nm)) names.push(nm);
  }
  if (names.length) signersCtx.setSigners('dr_Signers', names.map((n) => mkSigner(n)));
}

watch(() => props.chatFormData, (fd) => {
  const cd = t(fd?.entityCd || fd?.icpCd);
  if (cd && !editMode.value) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd && !editMode.value) void autofillFromEntity(cd); }, { immediate: true });

onMounted(async () => {
  emitProgress();
  try {
    const cfg: any = await getTaskConfigByCode(t(props.selection.task.TASK_CODE));
    taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : [];
  } catch { /* ignore */ }
});

// ── Edit 지원 ──
function gatherState() {
  return { values: { ...fill.exportPayload() }, signers: signersCtx.exportAll(), entityCd: entityCd.value, dpoGender: dpoGender.value, dpoContactCd: dpoContactCd.value };
}
async function restoreState(s: any) {
  if (!s) return;
  if (s.dpoGender === 'F' || s.dpoGender === 'M') dpoGender.value = s.dpoGender;
  if (s.values && typeof s.values === 'object') {
    for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v));
  }
  dpoContactName = t(s.values?.dpo_name);
  dpoContactCd.value = t(s.dpoContactCd);
  if (s.signers) signersCtx.importAll(s.signers);
  if (!signersCtx.signersOf('dr_Signers').length) seedDirectorSigners(await loadOfficers(t(s.entityCd) || entityCd.value));
  emitProgress();
}

// ── Create / Update ──
const REQUIRED: Array<[string, string]> = [
  ['company_name', 'company'],
  ['dpo_name', 'Data Protection Officer'],
  ['resolution_date', 'resolution date'],
  // DPO 선임일 = 위임장 날짜(DATE:) — Register 단계(레지스터·L_OFFICER)에 필요
  ['authorisation_date', 'letter of authorisation date (DATE:)'],
];

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  if (!entityCd.value) { progress.value = 'Please select a company first.'; return; }
  const v = fill.exportPayload();
  const missing = REQUIRED.filter(([k]) => !t(v[k])).map(([, label]) => label);
  if (missing.length) { progress.value = `Please fill in: ${missing.join(', ')}.`; return; }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = t(props.selection.task.TASK_CODE) || TASK_CODE;
    const boardSigners = signersCtx.signersOf('dr_Signers');
    const dpoSigner = mkSigner(t(v.dpo_name), t(v.dpo_designation), 'contact');
    const docFormData: Record<string, any> = {
      company_name: t(v.company_name),
      company_registration_no: t(v.company_registration_no),
      registered_office_address: t(v.registered_office_address),
      is_sole_director: boardSigners.length === 1,
      dpo_name: t(v.dpo_name),
      dpo_gender: dpoGender.value,
      dpo_designation: t(v.dpo_designation),
      dpo_contact_no: t(v.dpo_contact_no),
      dpo_email: t(v.dpo_email),
      lodgement_date: t(v.lodgement_date),
      company_mainline: t(v.company_mainline),
      resolution_date: t(v.resolution_date),
      authorisation_date: t(v.authorisation_date),
      // 결의 서명란 [[Signers]] = 이사 전원 — 직접 넣어 두면 백엔드가 DocSave.signers(이사 + DPO, T-Sign 참여자)로 덮지 않는다.
      Signers: boardSigners.length ? JSON.stringify(boardSigners) : '',
      signers: boardSigners.length ? JSON.stringify(boardSigners) : '',
      // 위임장 서명란 [[dpo_signature]] = DPO 본인
      dpo_signature: JSON.stringify([dpoSigner]),
    };
    const taskFormData = {
      taskTypeCd,
      entityCd: entityCd.value,
      companyName: docFormData.company_name,
      companyRegistrationNo: docFormData.company_registration_no,
      dpoName: docFormData.dpo_name,
      dpoDesignation: docFormData.dpo_designation,
      dpoContactNo: docFormData.dpo_contact_no,
      dpoEmail: docFormData.dpo_email,
      lodgementDate: docFormData.lodgement_date,
      resolutionDate: docFormData.resolution_date,
      // Register 단계 — Register of DPOs + L_OFFICER (registerToRegisterStore / register-officer 가 읽는 키)
      contactCd: dpoContactCd.value,
      officerName: docFormData.dpo_name,
      position: POS_DPO,
      appointedDate: docFormData.authorisation_date,
      presetForm: gatherState(),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: taskFormData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value,
          taskFlow: {
            createDocument: steps.length === 0 ? true : steps.includes('doc'),
            requestTSign: steps.includes('t-sign'),
            proceedWithEFiling: steps.includes('e-filing') || steps.includes('efiling'),
          },
          officerData: taskFormData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0) || null;
      if (!taskId) { progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`; return; }
    }

    progress.value = 'Saving documents…';
    // T-Sign 참여자 = 이사 전원 + DPO
    const tsignSigners = [...boardSigners, ...(docFormData.dpo_name ? [dpoSigner] : [])];
    const seen = new Set<number>();
    const docsToSave = (props.selection.documents || [])
      .filter((d: any) => {
        const tid = Number(d?.tmpId || 0);
        if (!tid || seen.has(tid)) return false;
        seen.add(tid); return true;
      })
      .map((d: any) => ({
        templateId: Number(d.tmpId),
        label: String(d.templateName || d.docCode || 'Document').replace(/\.docx?$/i, '').trim(),
        personName: docFormData.dpo_name || docFormData.company_name,
        formData: { ...docFormData },
        signers: tsignSigners.length ? tsignSigners : undefined,
        entityCd: entityCd.value,
      }));
    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((r) => r.json());
        if (!saveRes?.success) progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
      } catch (err: any) { progress.value = `⚠ Document save failed: ${err?.message || err}`; }
    }
    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="dpo-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Appointment of DPO' }}</div>
        <div class="co-subtitle">Directors' Resolutions — Appointment of Data Protection Officer &amp; Letter of Authorisation (Singapore)</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>

    <div class="co-doc">
      <AppointDpoPackageDoc v-model:gender="dpoGender" :sole-director="soleDirector" />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || !isReady || creating" @click="createTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.dpo-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.dpo-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
